/**
 * Alerts Page - Phase H
 * =======================
 * Display all alerts with filtering, detail view, and "Explain This Alert" functionality
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import GlobalHeader from '../components/GlobalHeader';
import { useTenant } from '../contexts/TenantContext';
import {
  AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp,
  ChevronRight, Loader2, Play, RefreshCw, Filter, Search,
  MessageSquare, BarChart3, Store, ShoppingCart, Truck,
  HeadphonesIcon, Brain, Sparkles, Clock, Calendar, ExternalLink
} from 'lucide-react';
import { Link } from 'wouter';

// ============================================================================
// TYPES
// ============================================================================

interface AlertRecord {
  id: string;
  companyId: string;
  alertType: string;
  scope: string;
  entityId: string | null;
  title: string;
  description: string;
  severity: number;
  confidence: number;
  detectedAt: string;
  resolvedAt?: string | null;
  metadata?: Record<string, any>;
}

interface AlertExplanation {
  explanation: string;
  recommendations: string[];
  agentId: string;
  agentName: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORIES = [
  { id: 'all', label: 'All Categories', icon: AlertTriangle },
  { id: 'social', label: 'Social', icon: MessageSquare },
  { id: 'competitor', label: 'Competitor', icon: BarChart3 },
  { id: 'retail', label: 'Retail', icon: Store },
  { id: 'dtc', label: 'DTC/Marketing', icon: ShoppingCart },
  { id: 'cx', label: 'Customer Experience', icon: HeadphonesIcon },
  { id: 'ops', label: 'Operations', icon: Truck },
];

const SEVERITY_OPTIONS = [
  { value: 0, label: 'All Severities' },
  { value: 5, label: 'Critical Only' },
  { value: 4, label: 'High & Above' },
  { value: 3, label: 'Medium & Above' },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getSeverityBadge(severity: number) {
  const config: Record<number, { color: string; label: string }> = {
    5: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Critical' },
    4: { color: 'bg-orange-100 text-orange-700 border-orange-200', label: 'High' },
    3: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Medium' },
    2: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Low' },
    1: { color: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Info' },
  };
  const { color, label } = config[severity] || config[3];
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}>
      {label}
    </span>
  );
}

function getConfidenceBadge(confidence: number) {
  const pct = Math.round(confidence * 100);
  let color = 'bg-green-100 text-green-700';
  if (pct < 50) color = 'bg-red-100 text-red-700';
  else if (pct < 70) color = 'bg-yellow-100 text-yellow-700';
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {pct}% confidence
    </span>
  );
}

function getCategoryFromAlertType(alertType: string): string {
  if (alertType.startsWith('social_')) return 'social';
  if (alertType.startsWith('competitor_')) return 'competitor';
  if (alertType.startsWith('retail_')) return 'retail';
  if (alertType.startsWith('dtc_')) return 'dtc';
  if (alertType.startsWith('cx_')) return 'cx';
  if (alertType.startsWith('ops_')) return 'ops';
  return 'unknown';
}

function getCategoryIcon(alertType: string) {
  const category = getCategoryFromAlertType(alertType);
  switch (category) {
    case 'social': return <MessageSquare className="w-5 h-5 text-pink-600" />;
    case 'competitor': return <BarChart3 className="w-5 h-5 text-red-600" />;
    case 'retail': return <Store className="w-5 h-5 text-purple-600" />;
    case 'dtc': return <ShoppingCart className="w-5 h-5 text-blue-600" />;
    case 'cx': return <HeadphonesIcon className="w-5 h-5 text-teal-600" />;
    case 'ops': return <Truck className="w-5 h-5 text-amber-600" />;
    default: return <AlertTriangle className="w-5 h-5 text-gray-600" />;
  }
}

function getAgentLinkForAlert(alertType: string): { path: string; name: string } {
  const category = getCategoryFromAlertType(alertType);
  switch (category) {
    case 'social': return { path: '/agents/social-listening', name: 'Social Listening Agent' };
    case 'competitor': return { path: '/agents/competitor-intel', name: 'Competitor Intel Agent' };
    case 'retail': return { path: '/agents/retail-story', name: 'Retail Story Agent' };
    case 'dtc': return { path: '/agents/marketing-mix', name: 'Marketing Mix Agent' };
    case 'cx': return { path: '/agents/cx-intel', name: 'CX Intelligence Agent' };
    case 'ops': return { path: '/agents/supply-chain', name: 'Supply Chain Agent' };
    default: return { path: '/agent-hub', name: 'Agent Hub' };
  }
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// ALERT ROW COMPONENT
// ============================================================================

interface AlertRowProps {
  alert: AlertRecord;
  isExpanded: boolean;
  onToggle: () => void;
  onExplain: () => void;
  isExplaining: boolean;
  explanation?: AlertExplanation | null;
}

function AlertRow({ alert, isExpanded, onToggle, onExplain, isExplaining, explanation }: AlertRowProps) {
  const agentLink = getAgentLinkForAlert(alert.alertType);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Main Row */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start gap-4">
          {/* Category Icon */}
          <div className="p-2 bg-gray-50 rounded-lg">
            {getCategoryIcon(alert.alertType)}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {getSeverityBadge(alert.severity)}
              {getConfidenceBadge(alert.confidence)}
              <span className="text-xs text-gray-500">
                {getCategoryFromAlertType(alert.alertType)}
              </span>
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-0.5">
              {alert.title}
            </h3>
            <p className="text-sm text-gray-600 line-clamp-1">
              {alert.description}
            </p>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatDate(alert.detectedAt)}
            </div>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Details */}
            <div className="space-y-3">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Description</h4>
                <p className="text-sm text-gray-700">{alert.description}</p>
              </div>

              {alert.entityId && alert.entityId !== '__NULL__' && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Entity</h4>
                  <p className="text-sm text-gray-700">{alert.entityId}</p>
                </div>
              )}

              {alert.metadata && Object.keys(alert.metadata).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Metadata</h4>
                  <pre className="text-xs bg-white p-2 rounded border border-gray-200 overflow-auto max-h-32">
                    {JSON.stringify(alert.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {/* Right: Actions & Explanation */}
            <div className="space-y-3">
              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onExplain();
                  }}
                  disabled={isExplaining}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 disabled:opacity-50 transition-colors"
                >
                  {isExplaining ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Brain className="w-4 h-4" />
                  )}
                  Explain This Alert
                </button>

                <Link href={agentLink.path}>
                  <a 
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-4 h-4" />
                    {agentLink.name}
                  </a>
                </Link>
              </div>

              {/* Explanation */}
              {explanation && (
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-semibold text-purple-700">
                      {explanation.agentName} Analysis
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">
                    {explanation.explanation}
                  </p>
                  {explanation.recommendations.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                        Recommended Actions
                      </h5>
                      <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                        {explanation.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AlertsPage() {
  const { companyId } = useTenant();
  const queryClient = useQueryClient();
  
  // State
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAlertId, setExpandedAlertId] = useState<string | null>(null);
  const [explanations, setExplanations] = useState<Record<string, AlertExplanation>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');

  // Fetch alerts
  const { data: alerts, isLoading, error, refetch } = useQuery<AlertRecord[]>({
    queryKey: ['alerts-active', companyId, categoryFilter, severityFilter],
    queryFn: async () => {
      let url = '/api/alerts/active';
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.set('category', categoryFilter);
      if (severityFilter > 0) params.set('severity', String(severityFilter));
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch alerts');
      const json = await response.json();
      return json.alerts || [];
    },
  });

  // Explain mutation
  const explainMutation = useMutation({
    mutationFn: async (alertId: string) => {
      const response = await fetch('/api/alerts/explain', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alertId }),
      });
      if (!response.ok) throw new Error('Failed to explain alert');
      return response.json();
    },
    onSuccess: (data, alertId) => {
      setExplanations(prev => ({
        ...prev,
        [alertId]: data.data,
      }));
    },
  });

  // Run alerts
  const runAlerts = async () => {
    setIsRunning(true);
    try {
      await Promise.all([
        fetch('/api/alerts/run-daily', { method: 'POST' }),
        fetch('/api/alerts/run-weekly', { method: 'POST' })
      ]);
      await queryClient.invalidateQueries({ queryKey: ['alerts-active'] });
      refetch();
    } catch (error) {
      console.error('Failed to run alerts:', error);
    } finally {
      setIsRunning(false);
    }
  };

  // Filter alerts
  const filteredAlerts = alerts?.filter(alert => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!alert.title.toLowerCase().includes(query) && 
          !alert.description.toLowerCase().includes(query)) {
        return false;
      }
    }
    return true;
  }) || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader
        title="Alerts"
        subtitle="Proactive intelligence from your data"
      />

      {/* Header Actions */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Left: Filters */}
            <div className="flex items-center gap-4">
              {/* Category Filter */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Severity Filter */}
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(parseInt(e.target.value))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {SEVERITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search alerts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-64"
                />
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={runAlerts}
                disabled={isRunning}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {isRunning ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Run Alert Evaluation
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'active'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Active Alerts
              {filteredAlerts.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                  {filteredAlerts.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'history'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              History
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load alerts</h3>
            <p className="text-gray-500 mb-4">There was an error fetching your alerts.</p>
            <button
              onClick={() => refetch()}
              className="text-purple-600 hover:underline"
            >
              Try again
            </button>
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className="text-center py-16">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">All Clear!</h3>
            <p className="text-gray-500 mb-4">
              No active alerts at this time. Your data looks healthy.
            </p>
            <button
              onClick={runAlerts}
              disabled={isRunning}
              className="text-purple-600 hover:underline"
            >
              Run alert evaluation to check for new issues
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAlerts.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                isExpanded={expandedAlertId === alert.id}
                onToggle={() => setExpandedAlertId(
                  expandedAlertId === alert.id ? null : alert.id
                )}
                onExplain={() => explainMutation.mutate(alert.id)}
                isExplaining={explainMutation.isPending && explainMutation.variables === alert.id}
                explanation={explanations[alert.id]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

